'use strict';

var _ = require('lodash');
var turfArea = require('turf-area');
var turfLineDistance = require('turf-line-distance');
var turfInside = require('turf-inside');
var turfCentroid = require('turf-centroid');

module.exports = {

  /**
   * Returns the area of a feature in square meters
   *
   * @param  {String} type Special feature type (only accepts circle)
   * @param  {Object} feature Feature GeoJSON
   * @return {Float} Area in square meters
   */
  area: function (type, feature) {
    if (type === 'circle') {
      // Calculates the approximate area of the circle
      // NOTE: the larger the circle gets the more it will be off because we are
      // not accounting for the curvature of the earth
      var radius = feature.properties.radius;
      return Math.PI * Math.pow(radius, 2);
    } else {
      return turfArea(feature);
    }
  },

  /*
   * Returns the latlng location of the centroid for the feature or featureGroup
   *
   * @param  {String} type Feature type (should work with all geojson features)
   * @param  {Object} feature Feature GeoJSON
   * @return {LatLng} Latlng object corresponding to the centroid
   */
  centroid : function (type, feature) {
    // For some reason, coordinates are as (lng, lat), so we need to reverse
    var coords = turfCentroid(feature).geometry.coordinates;
    return L.latLng(coords.reverse());
  },

  /*
   * Returns the latlng location as: the lat of the centroid and the lng of the top point
   *
   * @param  {String} type Feature type (should work with all geojson features)
   * @param  {Object} feature Feature GeoJSON
   * @return {LatLng} Latlng object corresponding to the centroid
   */
  centroidTop : function(type, feature) {
    // For some reason, coordinates are as (lng, lat), so we need to reverse

    var upperTwoPoints = feature.geometry.coordinates[0]
      .map(function(coord) {
        return coord;
      })
      .sort(function(coordA, coordB) {
        return coordB[1] - coordA[1];
      })
      .slice(0,2)
      .reduce(function(res, coord) {
        // return res === 0 ? (res + 4 * coord / 5) : (res + coord / 5);

        if (res.lat === 0) {
          return {
            lat: coord[0] * 4 / 5,
            lng: coord[1] * 4 / 5
          };
        } else {
          return {
            lat: res.lat + coord[0] * 1 / 5,
            lng: res.lng + coord[1] * 1 / 5
          };
        }
      }, {lat: 0, lng: 0});

    return L.latLng([upperTwoPoints.lng, upperTwoPoints.lat]);
  },

  /**
   * Returns the circumference of a feature in meters
   *
   * @param  {String} type Special feature type (only accepts circle)
   * @param  {Object} feature Feature GeoJSON
   * @return {Float} Circumference in meters
   */
  circumference: function (type, feature) {
    if (type === 'circle') {
      var radius = feature.properties.radius;
      return 2 * Math.PI * radius;
    } else {

      // create LineString using outer coordinates of the polygon
      var coords = feature.geometry.coordinates[0].slice();

      // make sure the first and last coordinate are the same to complete
      if (!_.isEqual(coords[0], coords[coords.length - 1])) {
        coords.push(coords[0].slice());
      }

      var ls = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coords
        }
      }

      return this.lineDistance(ls, 'meters');
    }
  },

  /**
   * Retrieve the distance of a LineString
   *
   * @param  {Object} ls LineString GeoJSON
   * @return {Number} The distance of the line string in a unit of choice
   */
  lineDistance: function (ls, units) {
    var distance;

    if (units === 'meters') {
      distance = turfLineDistance(ls, 'kilometers') * 1000;
    } else {
      distance = turfLineDistance(ls, units);
    }

    return distance;
  },

  inside: function (point, polygon) {
    return turfInside(point, polygon);
  },

  /**
   * Calculate the intersection of two lines.
   * NOTE: Does not determine if they HAVE intersected.. only where they will if
   * they do at all.
   *
   * @see  https://www.topcoder.com/community/data-science/data-science-tutorials/geometry-concepts-line-intersection-and-its-applications/
   *
   * @param  {Array.Array} line1 Array of two [x,y] or [lng,lat] points
   * @param  {Array.Array} line2 Array of two [x,y] or [lng,lat] points
   * @return {Array|Boolean} Intersection if it exists OR false if it does not
   */
  lineIntersection: function (line1, line2) {
    // calculate a, b, and c values for line equation in the standard form (Ax + By = C)
    var a1 = a(line1);
    var a2 = a(line2);
    var b1 = b(line1);
    var b2 = b(line2);
    var c1 = c(a1, b1, line1);
    var c2 = c(a2, b2, line2);

    // calculate the determinant
    var det = a1 * b2 - a2 * b1;

    if (det === 0) {
      // lines are parallel
      return false;
    } else {
      // Lines will intersect... calculate the intersection
      var x = (b2 * c1 - b1 * c2) / det;
      var y = (a1 * c2 - a2 * c1) / det;

      return [x, y];
    }

    function a (line) {
      return line[1][1] - line[0][1];
    }

    function b (line) {
      return line[0][0] - line[1][0];
    }

    function c (a, b, line) {
      return a * line[0][0] + b * line[0][1];
    }
  },

  /**
   * Tests if line segment `line1` intersects with line segment `line2`. Will
   * return the intersection if they do actually intersect.
   *
   * @param  {Array.Array} line1 Endpoints of a line segment
   * @param  {Array.Array} line2 Endpoints of a line segment
   * @return {Array|Boolean} Intersection if it does intersect OR false
   */
  lineSegmentIntersection: function (line1, line2) {
    var intersection = this.lineIntersection(line1, line2);

    if (intersection) {
      // check to see if the intersection is on BOTH line segments
      return lineContainsPoint(intersection, line1) &&
        lineContainsPoint(intersection, line2) &&
        intersection;
    } else {
      return false;
    }

    /**
     * Returns true if the point is on the line segment. Assumes that point is
     * already colinear.
     *
     * @param  {Array} point [x,y]
     * @param  {Array.Array} line Array of [x, y]
     * @return {Boolean}
     */
    function lineContainsPoint (point, line) {

      return within(point[0], line[0][0], line[1][0]) &&
        within(point[1], line[0][1], line[1][1]);

      /**
       * Check if `p` is approximately between `start` and `end`. We need to do
       * some fudging because the intersection calculation can very slightly change
       * the x or y value.
       *
       * @param  {Number} p
       * @param  {Number} start
       * @param  {Number} end
       * @return {Boolean}
       */
      function within (p, start, end) {
        // adjust all to 10 significant digits
        start = start.toFixed(10);
        end = end.toFixed(10);
        p = p.toFixed(10);
        return (start <= p && p <= end) || (end <= p && p <= start);
      }
    }
  },

  /**
   * Check to see if the linesegment collides with a polygon or polyline feature
   *
   * @param  {Array.Array} segment LineSegment as array of lnglat
   * @param  {Object} feature Polygon or Polyline as GeoJSON
   * @return {Boolean} True if the line segment collides with the target feature
   */
  lineSegmentCollides: function (segment, feature) {
    var collides = false;
    var coords = feature.geometry.coordinates[0].slice();
    var edge = [coords[0]]; // start with the first coord

    // Check if start and end are the same... if they are drop the last one
    // NOTE: This is needed because GeoJSON specs for polygon allow the last point
    // to be the same as the first (indicating that the polygon is complete), although
    // this does not seem to be strictly enforced.
    if (_.isEqual(coords[0], coords[coords.length -1])) {
      coords = coords.slice(0, -1);
    }

    while (!collides && coords.length > 0) {
      var coord = coords.pop();
      edge.push(coord);

      // build the edge to check
      if (edge.length < 2) {
        continue;
      }

      if (this.lineSegmentIntersection(segment, edge)) {
        collides = true;
      } else {
        // prepare for the next iteration... remove the first
        edge.shift();
      }
    }

    return collides;
  },

  /**
   * Determine whether or not a point about to be placed (drawn) or a segment
   * already drawn intersects with the specified feature.
   *
   * NOTE: Does not support circles, only polygons or polylines right now.
   *
   * @param  {Object} point GeoJSON Point
   * @param  {Object} linestring GeoJSON LineString
   * @param  {Object} feature GeoJSON Polygon or Polyline
   * @return {Boolean}
   */
  drawCollides: function (point, linestring, feature) {
    if (linestring) {
      var segment = linestring.geometry.coordinates;

      if (_.isEqual(segment[0], segment[1])) {
        return this.inside(point, feature);
      } else {
        return this.inside(point, feature) || this.lineSegmentCollides(segment, feature);
      }
    } else {
      return this.inside(point, feature);
    }
  }
};



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/layer/utils/Geometry.js